import {
  ActionIcon,
  Button,
  Flex,
  Group,
  InputLabel,
  InputWrapper,
  Modal,
  Space,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core"
import { useField } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import { modals, openConfirmModal } from "@mantine/modals"
import { showNotification } from "@mantine/notifications"
import {
  IconDeviceFloppy,
  IconDownload,
  IconListSearch,
  IconLock,
  IconLockOpen,
  IconLockOpen2,
  IconPdf,
  IconPencil,
  IconPlus,
  IconReport,
  IconUserStar,
  IconWorldStar,
} from "@tabler/icons-react"
import _ from "lodash"
import { usePostHog } from "posthog-js/react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import {
  DashboardRequests,
  DashboardResponses,
} from "@costory/types/endpoints/dashboard"
import { TreeNodeType } from "@costory/types/endpoints/folders"
import { DATE_PRESETS } from "@costory/types/filters"
import { ComponentType, DatePreset } from "@costory/types/prisma-client"

import { computePresetDates } from "@costory/shared/utils/filters"

import { DatePresetSelect } from "@costory/front/components/DatePresetSelect"
import { FileExplorer } from "@costory/front/components/FileExplorer/FileExplorer"
import { VdimValuesMultiSelect } from "@costory/front/components/MultiSelectWithChildrenOptions"
import { UnifiedActionBar } from "@costory/front/components/common/UnifiedActionBar"
import { Widget } from "@costory/front/components/dashboard/DashboardLayout"
import { runtimeConfig } from "@costory/front/lib/runtimeConfig"
import { EditScheduledDashboardConfigurationModal } from "@costory/front/pages/ScheduledDigestConfigurations/Dashboards/ConfigurationModal"
import { ScheduledDashboardList } from "@costory/front/pages/ScheduledDigestConfigurations/Dashboards/ScheduledDashboardList"
import { useAuthState } from "@costory/front/queries/auth"
import { useGeneratePdfFromDashboard } from "@costory/front/queries/dashboard"
import { useGetScheduledDashboardQuery } from "@costory/front/queries/scheduledDigestConfigurations"

import { SelectedVirtualDimensionsValuesSearchParams } from "./searchParamsSchema"
import { useDashboardSearchParams } from "./useDashboardSearchParams"

interface DashboardActionBarProps {
  widgets: Widget[]
  onAddWidget: () => void
  isEditing: boolean
  setIsEditing: (value: boolean) => void
  currentDashboard: DashboardResponses.GetDashboard | undefined
  isExistingDashboard: boolean
  onCreate: (
    payload: Omit<DashboardRequests.NewDashboard, "dashboardWidgets">,
  ) => Promise<void>
  isCreateDashboardMutationPending: boolean
  onUpdate: (
    payload: Omit<DashboardRequests.UpdateDashboard, "dashboardWidgets">,
  ) => Promise<void>
  onDiscardChanges: () => void
  isUpdateDashboardMutationPending: boolean
  setDashboardType: (dashboardType: ComponentType) => void
  dashboardType: ComponentType
  onDownloadPng: () => void
}

export const DashboardActionBar = ({
  widgets,
  isEditing,
  isExistingDashboard,
  setIsEditing,
  isCreateDashboardMutationPending,
  isUpdateDashboardMutationPending,
  onCreate,
  onUpdate,
  onAddWidget,
  onDiscardChanges,
  currentDashboard,
  dashboardType,
  setDashboardType,
  onDownloadPng,
}: DashboardActionBarProps) => {
  const [
    isSaveNewDashboardModalOpen,
    { close: closeSaveNewDashboardModal, open: openSaveNewDashboardModal },
  ] = useDisclosure()
  const [
    isFolderPickerModalOpen,
    { close: closeFolderPickerModal, open: openFolderPickerModal },
  ] = useDisclosure()
  const navigate = useNavigate()
  const [
    isDashboardPickerModalOpen,
    { close: closeDashboardPickerModal, open: openDashboardPickerModal },
  ] = useDisclosure()
  const [
    isScheduledReportModalOpen,
    { close: closeScheduledReportModal, open: openScheduledReportModal },
  ] = useDisclosure()
  const [
    isScheduledReportListModalOpen,
    {
      close: closeScheduledReportListModal,
      open: openScheduledReportListModal,
    },
  ] = useDisclosure()
  const [
    isRenameModalOpen,
    { close: closeRenameModal, open: openRenameModal },
  ] = useDisclosure()
  const auth = useAuthState()
  const posthog = usePostHog()
  const scheduledDashboardsQuery = useGetScheduledDashboardQuery()

  // Use unified search params handler
  const {
    dateRange: urlDateRange,
    datePreset,
    setDatePreset,
    setDatePresetWithDates,
    selectedVirtualDimensionsValues,
    setSelectedVirtualDimensionsValues,
  } = useDashboardSearchParams()

  const [dateRange, setDateRange] = useState<[string | null, string | null]>([
    urlDateRange[0],
    urlDateRange[1],
  ])

  useEffect(() => {
    setDateRange([urlDateRange[0], urlDateRange[1]])
  }, [urlDateRange])

  const pickerRef = useRef<HTMLButtonElement>(null)
  const generatePdfMutation = useGeneratePdfFromDashboard()
  const COSTORY_ORGID = runtimeConfig.VITE_COSTORY_ORGID
  const COSTORY_EMAIL_DOMAIN = runtimeConfig.VITE_COSTORY_EMAIL_DOMAIN

  const isAllowedToEdit =
    (currentDashboard &&
      currentDashboard.createdById == auth.user?.id &&
      currentDashboard?.type !== ComponentType.COMMUNITY) ||
    (auth.user?.isAdmin &&
      ((currentDashboard?.type == ComponentType.COMMUNITY &&
        auth.user?.currentOrg.id === COSTORY_ORGID &&
        auth.user?.email.endsWith(COSTORY_EMAIL_DOMAIN)) ||
        currentDashboard?.type == ComponentType.PUBLIC))

  const toggleDashboardType = (isAdmin?: boolean) => {
    setDashboardType(
      dashboardType === ComponentType.PUBLIC
        ? ComponentType.PRIVATE
        : dashboardType === ComponentType.PRIVATE
          ? isAdmin && auth.user?.currentOrg.id === COSTORY_ORGID
            ? ComponentType.COMMUNITY
            : ComponentType.PUBLIC
          : ComponentType.PUBLIC,
    )
  }

  const dashboardNameField = useField({
    initialValue: currentDashboard?.name ?? "",
  })

  const [defaultOrgDashboard, setDefaultOrgDashboard] = useState<boolean>(
    currentDashboard?.isOrgDefaultDashboard ?? false,
  )
  const [defaultDashboard, setDefaultDashboard] = useState<boolean>(
    currentDashboard?.isDefaultDashboard ?? false,
  )

  const [isTouched, setIsTouched] = useState(false)

  // Track changes to dashboard properties
  useEffect(() => {
    if (
      isExistingDashboard &&
      currentDashboard &&
      (dashboardType !== currentDashboard.type ||
        defaultDashboard !== currentDashboard.isDefaultDashboard ||
        defaultOrgDashboard !== currentDashboard.isOrgDefaultDashboard)
    ) {
      setIsTouched(true)
    } else {
      setIsTouched(false)
    }
  }, [
    dashboardType,
    defaultDashboard,
    defaultOrgDashboard,
    currentDashboard,
    isExistingDashboard,
  ])

  const handleVirtualDimensionValuesChange = (
    virtualDimensionsValues: string[],
  ) => {
    if (virtualDimensionsValues.length === 0) {
      setSelectedVirtualDimensionsValues([])
      return
    }

    const valuesGroupedByBqName = _.groupBy(
      virtualDimensionsValues,
      (value) => value.split(".")[0],
    )

    const formattedValues: SelectedVirtualDimensionsValuesSearchParams =
      Object.entries(valuesGroupedByBqName).map(([bqName, values]) => ({
        bqName,
        values: values.map((value) => value.split(".")[1]),
      }))

    setSelectedVirtualDimensionsValues(formattedValues)
  }

  const handleOpenDashboard = (dashboardId: string) => {
    navigate("/dashboards/" + dashboardId)
    closeDashboardPickerModal()
  }

  const handleOpenSaveNewDashboardModal = () => {
    if (isExistingDashboard) {
      dashboardNameField.setValue(currentDashboard!.name + " (copy)")
    }
    openSaveNewDashboardModal()
  }

  const handleCloseSaveNewDashboardModal = () => {
    if (isExistingDashboard) {
      dashboardNameField.setValue(currentDashboard!.name)
    }
    closeSaveNewDashboardModal()
  }

  const handleOpenFolder = async (folderId: string) => {
    if (dashboardNameField.getValue().trim() === "") {
      showNotification({
        title: "Error",
        message: "Please enter a name for your dashboard",
        color: "red",
      })
      return
    }
    if (isCreateDashboardMutationPending) {
      return
    }
    closeSaveNewDashboardModal()
    closeFolderPickerModal()
    await onCreate({
      name: dashboardNameField.getValue(),
      type: dashboardType,
      defaultDashboard,
      parentFolderId: folderId,
      defaultOrgDashboard: currentDashboard?.isOrgDefaultDashboard ?? false,
    })
  }

  const handleClickSaveChanges = async () => {
    if (isExistingDashboard) {
      await onUpdate({
        name: dashboardNameField.getValue(),
        type: dashboardType,
        defaultDashboard,
        defaultOrgDashboard,
        parentFolderId: currentDashboard!.parentFolderId,
      })
      setIsTouched(false)
      setIsEditing(false)
      return
    }

    if (!isExistingDashboard) {
      openFolderPickerModal()
      return
    }
  }

  const handleClickDefaultDashboard = () => {
    if (!defaultDashboard) {
      openConfirmModal({
        title: <Title size="h3">Set dashboard as personal default ?</Title>,
        children: (
          <Text size="sm">
            Setting this dashboard as your default, will remove the current
            default dashboard.
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => {
          setDefaultDashboard(true)
        },
      })
    } else {
      setDefaultDashboard(false)
    }
  }

  const handleClickDefaultOrgDashboard = () => {
    if (!defaultOrgDashboard) {
      openConfirmModal({
        title: (
          <Title size="h3">
            Set dashboard as organization&apos;s default ?
          </Title>
        ),
        children: (
          <Text size="sm">
            Setting this dashboard as the organization&apos;s default, will
            remove the current default dashboard.
          </Text>
        ),
        labels: { confirm: "Confirm", cancel: "Cancel" },
        onConfirm: () => {
          setDefaultOrgDashboard(true)
        },
      })
    } else {
      setDefaultOrgDashboard(false)
    }
  }

  const handleClickDiscardChanges = () => {
    dashboardNameField.reset()
    setDashboardType(currentDashboard?.type ?? ComponentType.PUBLIC)
    setDefaultOrgDashboard(currentDashboard?.isOrgDefaultDashboard ?? false)
    setDefaultDashboard(currentDashboard?.isDefaultDashboard ?? false)
    setIsTouched(false)
    onDiscardChanges()
    setIsEditing(false)
  }

  const handleRename = async () => {
    await onUpdate({
      name: dashboardNameField.getValue(),
      type: dashboardType,
      defaultDashboard,
      defaultOrgDashboard,
      parentFolderId: currentDashboard!.parentFolderId,
    })
    setIsTouched(false)
    closeRenameModal()
  }

  const handleSelectDateRange = useCallback(
    (dateRange: [string | null, string | null]) => {
      const [from, to] = dateRange
      setDateRange([from, to])

      if (from && to) {
        const currentParams = new URLSearchParams(window.location.search)
        currentParams.set("from", from)
        currentParams.set("to", to)
        currentParams.delete("datePreset")
        navigate(`?${currentParams.toString()}`, { replace: true })
      } else {
        const currentParams = new URLSearchParams(window.location.search)
        currentParams.delete("from")
        currentParams.delete("to")
        currentParams.delete("datePreset")
        navigate(`?${currentParams.toString()}`, { replace: true })
      }
    },
    [setDateRange, navigate],
  )

  const handleSelectDatePreset = useCallback(
    (selectedPreset: string | null | undefined) => {
      if (!selectedPreset) {
        setDatePreset(null)
        return
      }

      if (!DATE_PRESETS.includes(selectedPreset as DatePreset)) {
        console.warn(`Invalid DatePreset: ${selectedPreset}`)
        return
      }

      const presetDates = computePresetDates(selectedPreset as DatePreset)
      setDateRange([presetDates.from, presetDates.to])
      setDatePresetWithDates(selectedPreset, presetDates.from, presetDates.to)
    },
    [setDatePreset, setDatePresetWithDates, setDateRange],
  )

  const scheduledReportsForCurrentDashboard =
    scheduledDashboardsQuery.data?.filter(
      (config) => config.dashboardId === currentDashboard?.id,
    ) ?? []
  const hasScheduledReports = scheduledReportsForCurrentDashboard.length > 0

  return (
    <>
      <UnifiedActionBar
        isExisting={isExistingDashboard}
        isEditing={isEditing}
        onOpen={openDashboardPickerModal}
        onDuplicate={handleOpenSaveNewDashboardModal}
        settingsItems={
          isExistingDashboard && isAllowedToEdit
            ? [
                {
                  label: "Rename",
                  icon: <IconPencil />,
                  onClick: openRenameModal,
                },
                {
                  label:
                    dashboardType === ComponentType.PUBLIC
                      ? "Make private"
                      : dashboardType === ComponentType.COMMUNITY
                        ? "Make public"
                        : COSTORY_ORGID === auth.user?.currentOrg.id &&
                            auth.user?.isAdmin
                          ? "Make community"
                          : "Make public",
                  icon:
                    dashboardType === ComponentType.PUBLIC ? (
                      <IconLockOpen />
                    ) : dashboardType === ComponentType.COMMUNITY ? (
                      <IconLockOpen2 />
                    ) : (
                      <IconLock />
                    ),
                  color:
                    dashboardType === ComponentType.COMMUNITY
                      ? "success"
                      : dashboardType === ComponentType.PRIVATE
                        ? "textMuted"
                        : undefined,
                  onClick: () => toggleDashboardType(auth.user?.isAdmin),
                  disabled:
                    dashboardType === ComponentType.PUBLIC &&
                    currentDashboard?.createdById !== auth.user?.id,
                },
                {
                  label: currentDashboard?.isDefaultDashboard
                    ? "Your default dashboard"
                    : "Set as your default",
                  icon: <IconUserStar />,
                  onClick: handleClickDefaultDashboard,
                  disabled: currentDashboard?.isDefaultDashboard,
                },
                ...(auth.user!.isAdmin
                  ? [
                      {
                        label: currentDashboard?.isOrgDefaultDashboard
                          ? "Organization's default dashboard"
                          : "Set as organization default",
                        icon: <IconWorldStar />,
                        onClick: handleClickDefaultOrgDashboard,
                        disabled: currentDashboard?.isOrgDefaultDashboard,
                      },
                    ]
                  : []),
              ]
            : []
        }
        exportItems={[
          {
            label: "Download as PNG",
            icon: <IconDownload />,
            onClick: () => {
              onDownloadPng()
              posthog.capture("Downloaded dashboard as PNG", {
                dashboardId: currentDashboard?.id ?? "",
                dashboardName: currentDashboard?.name ?? "Dashboard",
                overrideVirtualDimensionsValues:
                  selectedVirtualDimensionsValues,
              })
            },
          },
          {
            label: "Download as PDF",
            icon: <IconPdf />,
            onClick: () => {
              generatePdfMutation.mutate({
                dashboardId: currentDashboard?.id ?? "",
                dashboardName: currentDashboard?.name ?? "Dashboard",
                overrideVirtualDimensionsValues:
                  selectedVirtualDimensionsValues,
              })
              posthog.capture("Downloaded dashboard as PDF", {
                dashboardId: currentDashboard?.id ?? "",
                dashboardName: currentDashboard?.name ?? "Dashboard",
                overrideVirtualDimensionsValues:
                  selectedVirtualDimensionsValues,
              })
            },
            disabled: generatePdfMutation.isPending,
          },
          {
            label: hasScheduledReports ? "Manage Reports" : "Schedule Report",
            icon: hasScheduledReports ? <IconListSearch /> : <IconReport />,
            onClick: hasScheduledReports
              ? openScheduledReportListModal
              : openScheduledReportModal,
          },
        ]}
        showEditButton={isExistingDashboard && isAllowedToEdit && !isTouched}
        onEdit={() => setIsEditing(true)}
        showDiscardButton={isTouched && isExistingDashboard && isAllowedToEdit}
        onDiscard={() => {
          modals.openConfirmModal({
            title: "Please confirm your action",
            children: (
              <Text size="sm">
                Are you sure you want to discard your changes?
              </Text>
            ),
            labels: { confirm: "Confirm", cancel: "Cancel" },
            onConfirm: handleClickDiscardChanges,
          })
        }}
        showSaveButton={
          (isTouched && isExistingDashboard && isAllowedToEdit) ||
          !isExistingDashboard
        }
        onSave={handleClickSaveChanges}
        saveButtonLabel="Save Changes"
        saveButtonLoading={isUpdateDashboardMutationPending}
        rightContent={
          isTouched ? undefined : (
            <>
              <VdimValuesMultiSelect
                value={selectedVirtualDimensionsValues.flatMap(
                  ({ values, bqName }) =>
                    values.map((value) => `${bqName}.${value}`),
                )}
                onChange={handleVirtualDimensionValuesChange}
                tooltip="Filter virtual dimensions values"
              />
              {isExistingDashboard && (
                <DatePresetSelect
                  value={datePreset}
                  onChange={handleSelectDatePreset}
                  from={urlDateRange[0] || null}
                  to={urlDateRange[1] || null}
                  onCustomDateChange={handleSelectDateRange}
                  customDateRange={dateRange}
                  pickerRef={pickerRef}
                  showLabel={false}
                  allowNull
                />
              )}
            </>
          )
        }
        editModeContent={
          <Group gap={4} justify="space-between">
            <Button
              variant="outline"
              leftSection={<IconPlus />}
              onClick={onAddWidget}
            >
              Add widget
            </Button>
            <Group gap={4}>
              {isExistingDashboard && (
                <Button
                  variant="transparent"
                  c="gray"
                  onClick={() => {
                    modals.openConfirmModal({
                      title: "Please confirm your action",
                      children: (
                        <Text size="sm">
                          Are you sure you want to discard your changes?
                        </Text>
                      ),
                      labels: { confirm: "Confirm", cancel: "Cancel" },
                      onConfirm: handleClickDiscardChanges,
                    })
                  }}
                >
                  Discard
                </Button>
              )}
              <Button
                loading={isUpdateDashboardMutationPending}
                disabled={!isExistingDashboard && widgets.length === 0}
                onClick={handleClickSaveChanges}
                leftSection={<IconDeviceFloppy />}
              >
                Save
              </Button>
            </Group>
          </Group>
        }
      />
      <Modal
        opened={isFolderPickerModalOpen}
        onClose={closeFolderPickerModal}
        title={<Title>Select a folder to save this dashboard to</Title>}
        size="80vw"
        styles={{ body: { height: "70vh" } }}
      >
        <Stack h="100%">
          <InputWrapper>
            <InputLabel>Name your Dashboard</InputLabel>
            <TextInput
              {...dashboardNameField.getInputProps()}
              placeholder="Enter dashboard name"
            />
          </InputWrapper>
          <FileExplorer
            onDoubleClick={(id, type) => {
              if (type !== TreeNodeType.Folder) {
                return
              }
              handleOpenFolder(id)
            }}
            onOpenFolder={handleOpenFolder}
            buttonProps={{
              label: "Save",
              loading: isCreateDashboardMutationPending,
            }}
            isInSaveMode
          />
        </Stack>
      </Modal>

      <Modal
        opened={isDashboardPickerModalOpen}
        onClose={closeDashboardPickerModal}
        title={<Title>Open an existing dashboard </Title>}
        size="80vw"
        styles={{ body: { height: "70vh" } }}
      >
        <Stack h="100%">
          <FileExplorer
            onDoubleClick={(id, type) => {
              if (type !== TreeNodeType.Dashboard) {
                return
              }
              handleOpenDashboard(id)
            }}
            onOpenDashboard={handleOpenDashboard}
            defaultShouldShowSavedViews={false}
            isInSaveMode={false}
          />
        </Stack>
      </Modal>

      <Modal
        opened={isSaveNewDashboardModalOpen}
        onClose={handleCloseSaveNewDashboardModal}
        title={<Title>Save a copy of this dashboard</Title>}
        size="80vw"
        styles={{ body: { height: "70vh" } }}
      >
        <Stack h="100%" gap={4}>
          <InputWrapper>
            <InputLabel fw="bold">Dashboard name</InputLabel>
            <Flex>
              <TextInput flex={1} {...dashboardNameField.getInputProps()} />
              <Tooltip label={_.upperFirst(dashboardType.toLowerCase())}>
                <ActionIcon
                  m={10}
                  mt={5}
                  onClick={() =>
                    toggleDashboardType(auth.user?.isAdmin ?? false)
                  }
                  c={
                    dashboardType === ComponentType.COMMUNITY
                      ? "success"
                      : dashboardType === ComponentType.PRIVATE
                        ? "textMuted"
                        : undefined
                  }
                >
                  {dashboardType === ComponentType.PUBLIC ? (
                    <IconLockOpen2 size="inherit" />
                  ) : dashboardType === ComponentType.COMMUNITY ? (
                    <IconLockOpen2 size="inherit" />
                  ) : (
                    <IconLock size="inherit" />
                  )}
                </ActionIcon>
              </Tooltip>
            </Flex>
          </InputWrapper>
          <Space h={8} />
          <InputLabel fw="bold">
            Select a folder to save this dashboard to
          </InputLabel>
          <Stack h="80%">
            <FileExplorer
              onOpenFolder={handleOpenFolder}
              onDoubleClick={(id, type) => {
                if (type !== TreeNodeType.Folder) {
                  return
                }
                handleOpenFolder(id)
              }}
              buttonProps={{
                label: "Save",
                loading: isCreateDashboardMutationPending,
              }}
              defaultShouldShowSavedViews={false}
              isInSaveMode
            />
          </Stack>
        </Stack>
      </Modal>

      <EditScheduledDashboardConfigurationModal
        opened={isScheduledReportModalOpen}
        onClose={closeScheduledReportModal}
        initialDashboardId={currentDashboard?.id}
      />

      <Modal
        size="90vw"
        opened={isScheduledReportListModalOpen}
        onClose={closeScheduledReportListModal}
        title={<Title>Scheduled Reports</Title>}
      >
        <ScheduledDashboardList
          onEdit={() => {
            closeScheduledReportListModal()
            openScheduledReportModal()
          }}
        />
      </Modal>

      <Modal
        opened={isRenameModalOpen}
        onClose={closeRenameModal}
        title={<Title>Rename dashboard</Title>}
        size="md"
      >
        <Stack>
          <TextInput
            label="Dashboard name"
            placeholder="Enter dashboard name"
            {...dashboardNameField.getInputProps()}
            data-autofocus
          />
          <Group justify="end">
            <Button variant="default" onClick={closeRenameModal}>
              Cancel
            </Button>
            <Button
              onClick={handleRename}
              disabled={!dashboardNameField.getValue().trim()}
              loading={isUpdateDashboardMutationPending}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
